import React, { useState, useEffect } from "react";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../services/apiService";
import { ConfirmDialog } from "../components/common/ConfirmDialog";
import { User, UserRole, Workflow } from "../types";
import toast from "../utils/toast";
import { useTranslation } from "react-i18next";

interface UserFormData {
  username: string;
  email: string;
  password: string;
  role: UserRole;
}

export const UserManagement: React.FC = () => {
  const { t } = useTranslation(["users", "common", "errors", "auth"]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [selectedWorkflowIds, setSelectedWorkflowIds] = useState<number[]>([]);
  const [projectInstances, setProjectInstances] = useState<any[]>([]);
  const [selectedWorkflowInstanceIds, setSelectedWorkflowInstanceIds] =
    useState<number[]>([]);
  const [visitorFinancialAccess, setVisitorFinancialAccess] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    username: "",
    email: "",
    password: "",
    role: UserRole.EMPLOYEE,
  });

  useEffect(() => {
    loadUsers();
    loadWorkflows();
    loadWorkflowInstances();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await apiService.getUsers();
      setUsers(data);
    } catch (error) {
      toast.error(t("users:loadUsersFailed"));
    } finally {
      setLoading(false);
    }
  };

  const loadWorkflows = async () => {
    try {
      const data = await apiService.getWorkflows();
      setWorkflows(data || []);
    } catch (error) {
    }
  };

  const loadWorkflowInstances = async () => {
    try {
      const data = await apiService.getWorkflowInstances();
      setProjectInstances(Array.isArray(data) ? data : []);
    } catch (error) {
      setProjectInstances([]);
    }
  };

  const getUserAllowedWorkflowIds = (user: User): number[] => {
    if (!user.settingsJson) {
      return [];
    }
    try {
      const settings = JSON.parse(user.settingsJson);
      const rawIds = settings?.workflowPermissions?.allowedWorkflowIds;
      if (!Array.isArray(rawIds)) {
        return [];
      }
      return rawIds
        .map((id: any) => Number.parseInt(String(id), 10))
        .filter((id: number) => !Number.isNaN(id));
    } catch (error) {
      return [];
    }
  };

  const getUserAllowedWorkflowInstanceIds = (user: User): number[] => {
    if (!user.settingsJson) {
      return [];
    }
    try {
      const settings = JSON.parse(user.settingsJson);
      const rawIds =
        settings?.workflowPermissions?.visitorAllowedWorkflowInstanceIds;
      if (!Array.isArray(rawIds)) {
        return [];
      }
      return rawIds
        .map((id: any) => Number.parseInt(String(id), 10))
        .filter((id: number) => !Number.isNaN(id));
    } catch (error) {
      return [];
    }
  };

  const getUserVisitorFinancialAccess = (user: User): boolean => {
    if (!user.settingsJson) {
      return false;
    }
    try {
      const settings = JSON.parse(user.settingsJson);
      return Boolean(settings?.workflowPermissions?.visitorFinancialAccess);
    } catch (error) {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: any = { ...formData };

      if (formData.role === UserRole.PROJECT_MANAGER) {
        if (selectedWorkflowIds.length === 0) {
          toast.error(t("users:selectAtLeastOneWorkflow"));
          return;
        }
        payload.allowedWorkflowIds = selectedWorkflowIds;
      }

      if (formData.role === UserRole.VISITOR) {
        payload.allowedWorkflowInstanceIds = selectedWorkflowInstanceIds;
        payload.visitorFinancialAccess = visitorFinancialAccess;
      }

      if (editingUser) {
        await apiService.updateUser(editingUser.id!, payload);
        toast.success(t("users:userUpdated"));
      } else {
        await apiService.createUser(payload);
        toast.success(t("users:userCreated"));
      }
      setShowForm(false);
      setEditingUser(null);
      resetForm();
      loadUsers();
    } catch (error) {
      toast.error(t("users:saveUserFailed"));
    }
  };

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete?.id) {
      return;
    }

    try {
      await apiService.deleteUser(userToDelete.id);
      toast.success(t("users:userDeleted"));
      await loadUsers();
    } catch (error) {
      toast.error(t("users:deleteUserFailed"));
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: "",
      role: user.role,
    });
    setSelectedWorkflowIds(getUserAllowedWorkflowIds(user));
    setSelectedWorkflowInstanceIds(getUserAllowedWorkflowInstanceIds(user));
    setVisitorFinancialAccess(getUserVisitorFinancialAccess(user));
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      username: "",
      email: "",
      password: "",
      role: UserRole.EMPLOYEE,
    });
    setSelectedWorkflowIds([]);
    setSelectedWorkflowInstanceIds([]);
    setVisitorFinancialAccess(false);
  };

  const getRoleLabel = (role: UserRole): string => {
    switch (role) {
      case UserRole.ADMIN:
        return t("users:roles.admin");
      case UserRole.MANAGER:
        return t("users:roles.manager");
      case UserRole.PROJECT_MANAGER:
        return t("users:roles.projectManager");
      case UserRole.INTERN:
        return t("users:roles.intern");
      case UserRole.EMPLOYEE:
        return t("users:roles.employee");
      case UserRole.VISITOR:
        return t("users:roles.visitor");
      default:
        return role;
    }
  };

  const getRoleBadgeClass = (role: UserRole): string => {
    switch (role) {
      case UserRole.ADMIN:
        return "bg-purple-100 text-purple-800";
      case UserRole.MANAGER:
        return "bg-green-100 text-green-800";
      case UserRole.PROJECT_MANAGER:
        return "bg-indigo-100 text-indigo-800";
      case UserRole.INTERN:
        return "bg-amber-100 text-amber-800";
      case UserRole.EMPLOYEE:
        return "bg-blue-100 text-blue-800";
      case UserRole.VISITOR:
        return "bg-slate-100 text-slate-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingUser(null);
    resetForm();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t("users:title")}</h1>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          {t("users:createUser")}
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {editingUser ? t("users:editUser") : t("users:createUser")}
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("auth:username")}
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("auth:email")}
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("auth:password")}{" "}
                  {editingUser && `(${t("common:optional")})`}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required={!editingUser}
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("auth:role")}
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => {
                    const nextRole = e.target.value as UserRole;
                    setFormData({
                      ...formData,
                      role: nextRole,
                    });
                    if (nextRole !== UserRole.PROJECT_MANAGER) {
                      setSelectedWorkflowIds([]);
                    }
                    if (nextRole !== UserRole.VISITOR) {
                      setSelectedWorkflowInstanceIds([]);
                      setVisitorFinancialAccess(false);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={UserRole.EMPLOYEE}>
                    {t("users:roles.employee")}
                  </option>
                  <option value={UserRole.VISITOR}>
                    {t("users:roles.visitor")}
                  </option>
                  <option value={UserRole.PROJECT_MANAGER}>
                    {t("users:roles.projectManager")}
                  </option>
                  <option value={UserRole.INTERN}>
                    {t("users:roles.intern")}
                  </option>
                  <option value={UserRole.MANAGER}>
                    {t("users:roles.manager")}
                  </option>
                  <option value={UserRole.ADMIN}>
                    {t("users:roles.admin")}
                  </option>
                </select>
              </div>
              {formData.role === UserRole.PROJECT_MANAGER && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("users:projectManagerWorkflows")}
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    {t("users:projectManagerWorkflowsDescription")}
                  </p>
                  {workflows.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      {t("users:noWorkflowsFound")}
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2">
                      {workflows.map((workflow) => {
                        const isChecked = selectedWorkflowIds.includes(
                          workflow.id
                        );
                        return (
                          <label
                            key={workflow.id}
                            className="flex items-center gap-2 text-sm text-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedWorkflowIds((prev) => [
                                    ...prev,
                                    workflow.id,
                                  ]);
                                } else {
                                  setSelectedWorkflowIds((prev) =>
                                    prev.filter((id) => id !== workflow.id)
                                  );
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span>{workflow.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {formData.role === UserRole.VISITOR && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("users:visitorProjects", "Projeler")}
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    {t(
                      "users:visitorProjectsDescription",
                      "Ziyaretçinin görebileceği projeleri seçin"
                    )}
                  </p>
                  {projectInstances.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      {t("users:noProjectsFound", "Proje bulunamadı")}
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2">
                      {projectInstances.map((instance: any) => {
                        const id = Number(instance?.id);
                        const name =
                          instance?.workflowContainerName ||
                          instance?.name ||
                          String(instance?.id ?? "-");
                        const isChecked =
                          !Number.isNaN(id) &&
                          selectedWorkflowInstanceIds.includes(id);
                        return (
                          <label
                            key={String(instance?.id)}
                            className="flex items-center gap-2 text-sm text-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              disabled={Number.isNaN(id)}
                              onChange={(e) => {
                                if (Number.isNaN(id)) {
                                  return;
                                }
                                if (e.target.checked) {
                                  setSelectedWorkflowInstanceIds((prev) => [
                                    ...prev,
                                    id,
                                  ]);
                                } else {
                                  setSelectedWorkflowInstanceIds((prev) =>
                                    prev.filter((x) => x !== id)
                                  );
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span>{name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  <label className="mt-3 flex items-start gap-2 text-xs text-gray-700">
                    <input
                      type="checkbox"
                      checked={visitorFinancialAccess}
                      onChange={(e) =>
                        setVisitorFinancialAccess(e.target.checked)
                      }
                      className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span>
                      <span className="font-semibold">
                        {t(
                          "users:visitorFinancialAccess",
                          "Fatura ve teklif bilgilerini görüntüleyebilir"
                        )}
                      </span>
                      <br />
                      <span className="text-gray-500">
                        {t(
                          "users:visitorFinancialAccessDescription",
                          "İşaretlenirse ziyaretçi fatura/teklif dosyalarını ve iş tutarı-teklif miktarını görebilir."
                        )}
                      </span>
                    </span>
                  </label>
                </div>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancelForm}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t("common:cancel")}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  {editingUser ? t("common:update") : t("common:create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {users.map((user) => (
            <li key={user.id}>
              <div className="px-4 py-4 flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <UserIcon className="h-10 w-10 text-gray-400" />
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center">
                      <h3 className="text-lg font-medium text-gray-900">
                        {user.username}
                      </h3>
                      <span
                        className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeClass(
                          user.role
                        )}`}
                      >
                        {getRoleLabel(user.role)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">{user.email}</p>
                    <p className="text-xs text-gray-400">
                      Created:{" "}
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString()
                        : "N/A"}
                    </p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditUser(user)}
                    className="text-blue-400 hover:text-blue-600"
                    title={t("users:editUser")}
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteUser(user)}
                    className="text-red-400 hover:text-red-600"
                    title={t("users:deleteUser")}
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <ConfirmDialog
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={confirmDeleteUser}
        title={t("users:deleteUser")}
        message={t("users:confirmDelete")}
        confirmText={t("common:delete")}
        cancelText={t("common:cancel")}
        type="danger"
      />
    </div>
  );
};
